import { useEffect, useState } from "react";
import { api, errMsg } from "../lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Target, Bot, AlertTriangle, TrendingUp, Sparkles, BookOpen, Film, Newspaper, Users, ListChecks, ExternalLink, CalendarCheck } from "lucide-react";

const PRIORITY_STYLE = {
  alta: "bg-red-100 text-red-700 border-red-200",
  media: "bg-amber-100 text-amber-700 border-amber-200",
  baja: "bg-green-100 text-green-700 border-green-200",
};
const LEVEL_STYLE = {
  obligatorio: "bg-red-600 text-white",
  preparacion_examen: "bg-purple-600 text-white",
  muy_recomendado: "bg-orange-600 text-white",
  recomendado: "bg-amber-500 text-white",
};

export default function Recommendations() {
  const [data, setData] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [busyAnalysis, setBusyAnalysis] = useState(false);

  useEffect(() => { api.get("/recommendations").then((r) => setData(r.data)).catch(() => {}); }, []);

  const runAnalysis = async () => {
    setBusyAnalysis(true);
    try {
      const r = await api.post("/recommendations/dakota-analysis");
      setAnalysis(r.data.analysis);
      if (r.data.cached) toast.info("Análisis de hoy reutilizado · 0 créditos");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusyAnalysis(false); }
  };

  if (!data) return <p className="text-neutral-500">Analizando tu progreso…</p>;
  const p = data.progress;

  return (
    <div className="fade-up space-y-8" data-testid="recommendations-page">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2"><Target className="text-orange-600" /> Recursos Recomendados para Mí</h1>
        <p className="text-neutral-500 mt-1">Tu mentor académico automático: recomendaciones basadas en tu actividad, sin consumir créditos.</p>
      </div>

      {data.motivation.length > 0 && (
        <div className="rounded-xl p-5 bg-green-50 border border-green-200" data-testid="motivation-banner">
          {data.motivation.map((m, i) => <p key={i} className="text-sm text-green-800 font-medium flex items-center gap-2"><Sparkles size={14} /> {m}</p>)}
        </div>
      )}

      {data.alerts.length > 0 && (
        <div className="space-y-2" data-testid="alerts-section">
          {data.alerts.map((a, i) => (
            <div key={i} className="flex items-start gap-3 rounded-xl p-4 bg-amber-50 border border-amber-200 text-sm text-amber-800">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" /> {a.text}
            </div>
          ))}
        </div>
      )}

      <section data-testid="progress-section">
        <h2 className="text-lg font-semibold mb-4">Mi Progreso</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {[["Nivel global", `${p.level}%`], ["Preguntas", p.questions], ["Aciertos", p.correct], ["Errores", p.errors],
            ["Temas completados", p.topics_completed], ["Temas pendientes", p.topics_pending], ["Racha", `${p.streak_days} días`]].map(([l, v]) => (
            <div key={l} className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-4">
              <p className="text-[10px] tracking-[0.15em] uppercase font-semibold text-neutral-500">{l}</p>
              <p className="text-xl font-semibold mt-1">{v}</p>
            </div>
          ))}
        </div>
        <div className="mt-3 h-2 bg-neutral-200 rounded-full overflow-hidden">
          <div className="h-full bg-orange-600 rounded-full transition-transform" style={{ width: `${p.level}%` }} />
        </div>
      </section>

      <div className="grid lg:grid-cols-2 gap-6">
        <section className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6" data-testid="weaknesses-section">
          <h2 className="font-semibold mb-4 flex items-center gap-2 text-red-700"><AlertTriangle size={17} /> Necesitas reforzar</h2>
          {data.weaknesses.length === 0 ? <p className="text-sm text-neutral-500">¡Sin debilidades detectadas!</p> : (
            <ol className="space-y-2">
              {data.weaknesses.map((w, i) => (
                <li key={w.topic_id} className="flex items-center justify-between text-sm border-b border-neutral-100 pb-2">
                  <span>{i + 1}. {w.name} {!w.attempted && <span className="text-neutral-400">(sin practicar)</span>}</span>
                  <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${PRIORITY_STYLE[w.priority]}`}>
                    Prioridad {w.priority} · {w.mastery}%
                  </span>
                </li>
              ))}
            </ol>
          )}
        </section>
        <section className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6" data-testid="strengths-section">
          <h2 className="font-semibold mb-4 flex items-center gap-2 text-green-700"><TrendingUp size={17} /> Tus mejores resultados</h2>
          {data.strengths.length === 0 ? <p className="text-sm text-neutral-500">Completa tests para descubrir tus fortalezas.</p> : (
            <ol className="space-y-2">
              {data.strengths.map((s, i) => (
                <li key={s.topic_id} className="flex items-center justify-between text-sm border-b border-neutral-100 pb-2">
                  <span>{i + 1}. {s.name}</span>
                  <span className="text-xs font-semibold text-green-700">{s.mastery}% dominio</span>
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>

      {data.featured.length > 0 && (
        <section data-testid="featured-section">
          <h2 className="text-lg font-semibold mb-4">Recomendados por tu profesor y franquicia</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.featured.map((f) => (
              <div key={f.id} className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-5" data-testid="featured-card">
                <span className={`text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full ${LEVEL_STYLE[f.level] || "bg-neutral-600 text-white"}`}>
                  {f.level_label}
                </span>
                <h3 className="font-semibold mt-3 text-sm">{f.title}</h3>
                <p className="text-xs text-neutral-500 mt-1">Destacado por {f.featured_by}</p>
                <a href={f.url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-sm text-orange-700 hover:underline">
                  Abrir <ExternalLink size={12} />
                </a>
              </div>
            ))}
          </div>
        </section>
      )}

      <section data-testid="recommendation-blocks">
        <h2 className="text-lg font-semibold mb-4">Recomendaciones personalizadas</h2>
        <div className="space-y-4">
          {data.recommendations.map((r) => (
            <div key={r.topic} className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6" data-testid={`rec-block-${r.topic.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="font-semibold">{r.topic}</h3>
                <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${PRIORITY_STYLE[r.priority]}`}>Prioridad {r.priority}</span>
              </div>
              <p className="text-sm text-neutral-500 mt-1">{r.reason}</p>
              <div className="mt-4 grid md:grid-cols-2 gap-2 text-sm">
                {r.resources.map((x) => <a key={x.id} href={x.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-lg border border-neutral-200 px-3 py-2 hover:bg-orange-50"><BookOpen size={14} className="text-orange-600 shrink-0" /> {x.title}</a>)}
                {r.multimedia.map((x) => <a key={x.id} href={x.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-lg border border-neutral-200 px-3 py-2 hover:bg-orange-50"><Film size={14} className="text-orange-600 shrink-0" /> {x.title}</a>)}
                {r.articles.map((x) => <Link key={x.id} to="/app/blog" className="flex items-center gap-2 rounded-lg border border-neutral-200 px-3 py-2 hover:bg-orange-50"><Newspaper size={14} className="text-orange-600 shrink-0" /> {x.h1}</Link>)}
                {r.groups.map((x) => <Link key={x.id} to="/app/comunidad" className="flex items-center gap-2 rounded-lg border border-neutral-200 px-3 py-2 hover:bg-orange-50"><Users size={14} className="text-orange-600 shrink-0" /> Debate: {x.title}</Link>)}
                <Link to="/app/tests" data-testid={`rec-test-${r.topic.toLowerCase().replace(/\s+/g, "-")}`}
                      className="flex items-center gap-2 rounded-lg border border-orange-300 bg-orange-50 px-3 py-2 font-medium text-orange-800 hover:bg-orange-100">
                  <ListChecks size={14} className="shrink-0" /> {r.test.label}
                </Link>
              </div>
            </div>
          ))}
          {data.recommendations.length === 0 && <p className="text-sm text-neutral-500">Haz tu primer test para recibir recomendaciones personalizadas.</p>}
        </div>
      </section>

      <section className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6" data-testid="study-plan-section">
        <h2 className="font-semibold mb-4 flex items-center gap-2"><CalendarCheck size={17} className="text-orange-600" /> Plan de estudio automático</h2>
        <div className="grid md:grid-cols-3 gap-6 text-sm">
          {[["Hoy", data.plan.daily], ["Esta semana", data.plan.weekly], ["Este mes", data.plan.monthly]].map(([label, items]) => (
            <div key={label}>
              <p className="text-xs tracking-[0.15em] uppercase font-semibold text-neutral-500 mb-2">{label}</p>
              <ul className="space-y-1.5">
                {items.map((it, i) => <li key={i} className="flex gap-2"><span className="text-orange-600">•</span> {it}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-xl p-6 dakota-glow bg-orange-50 border border-orange-100" data-testid="dakota-analysis-section">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-semibold flex items-center gap-2"><Bot size={18} className="text-orange-600" /> Analizar mi progreso con Dakota</h2>
            <p className="text-sm text-neutral-500 mt-1">Análisis avanzado y plan personalizado. Consume 1 crédito (1 análisis al día, luego gratuito).</p>
          </div>
          <Button data-testid="dakota-analysis-btn" onClick={runAnalysis} disabled={busyAnalysis}
                  className="rounded-full bg-orange-600 hover:bg-orange-700 text-white">
            {busyAnalysis ? "Dakota está analizando…" : "Analizar con Dakota"}
          </Button>
        </div>
        {analysis && (
          <div className="mt-4 bg-white rounded-xl border border-orange-100 p-5 text-sm whitespace-pre-wrap leading-relaxed" data-testid="dakota-analysis-result">
            {analysis}
          </div>
        )}
      </section>
    </div>
  );
}
